// functionsBase.js
//
// One place that decides where a Cloud Function call goes.
//
// Before this file, all 59 `fetch` calls to Cloud Functions across 25 files carried the production
// URL as a hardcoded string literal. That made them the one part of the app that could not be
// pointed anywhere else — src/firebase.js's REACT_APP_USE_EMULATOR switch redirects Firestore,
// Auth and Storage, but it has no reach over a bare `fetch` to an absolute https:// URL. So an app
// running entirely against the local emulator still sent every signup, every user creation, every
// payment intent and every AI draft to PRODUCTION. Browser E2E fixtures that create a company and
// its users through the real UI are what surfaced it; the exposure was always there.
//
// PRODUCTION BEHAVIOUR IS UNCHANGED, and in the strongest sense available: Create React App
// substitutes process.env.REACT_APP_USE_EMULATOR with a literal at BUILD time, so with the
// variable unset the ternary below folds to the production base and Terser drops the other branch
// entirely — the 127.0.0.1 address is absent from the shipped bundle, not merely unreachable. Same
// mechanism, and the same guarantee, as src/firebase.js's emulator wiring.
//
// The project id is deliberately the same constant in both branches. It is not configurable and
// must never change: it is the production project, and in the emulator it is only a local
// namespace. The Firestore, Auth and Functions emulators must all agree on it, which is why
// package.json's `emulators:e2e` script starts them with --project cpp-field-service-app rather
// than the taskwrx-test id the jest integration harness uses.
const PROJECT_ID = 'cpp-field-service-app';
const REGION = 'us-central1';

// Host:port of the Functions emulator. Overridable only so a second emulator instance on another
// port does not require a code change; the default matches firebase.json's functions emulator.
const EMULATOR_HOST = process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST || '127.0.0.1:5001';

// The Functions emulator serves at /<projectId>/<region>/<fn>, which deployed functions do not —
// hence a base rather than a swappable hostname.
export const FUNCTIONS_BASE = process.env.REACT_APP_USE_EMULATOR === 'true'
  ? 'http://' + EMULATOR_HOST + '/' + PROJECT_ID + '/' + REGION
  : 'https://' + REGION + '-' + PROJECT_ID + '.cloudfunctions.net';

// Every Cloud Function URL in the app is built here.
//
// Takes the function NAME, never a path or a full URL — passing a leading slash or a query string
// would produce a URL that looks right and 404s, so it throws instead. These are module-level
// constants in most call sites, so a bad name fails at import time rather than on the click that
// needed it.
export const fnUrl = (name) => {
  if (typeof name !== 'string' || name.length === 0 || name.includes('/') || name.includes('?')) {
    throw new Error('fnUrl expects a bare function name, received: ' + String(name));
  }
  return FUNCTIONS_BASE + '/' + name;
};
